export class MiniGames {
  private container: HTMLElement;
  private aimPanel: HTMLElement;
  private mathPanel: HTMLElement;

  // Aim booster state
  private aimActive = false;
  private aimTargets: HTMLElement[] = [];
  private aimHit = 0;
  private aimTotal = 15;
  private aimTimer = 0;
  private aimTimerInterval = 0;
  private aimTimerEl!: HTMLElement;
  private aimCountEl!: HTMLElement;
  private aimAreaEl!: HTMLElement;

  // Mental math state
  private mathOpen = false;
  private mathProblems: { question: string; answer: number }[] = [];
  private mathSolved = 0;
  private mathTotal = 10;
  private mathCurrentIdx = 0;
  private mathQuestionEl!: HTMLElement;
  private mathInputEl!: HTMLInputElement;
  private mathProgressEl!: HTMLElement;

  // Callbacks
  private onComboEarned: () => void;
  private onLifeEarned: () => void;
  private onOpened: () => void;

  constructor(
    container: HTMLElement,
    onComboEarned: () => void,
    onLifeEarned: () => void,
    onOpened?: () => void
  ) {
    this.container = container;
    this.onComboEarned = onComboEarned;
    this.onLifeEarned = onLifeEarned;
    this.onOpened = onOpened || (() => {});

    // Aim Booster Panel
    this.aimPanel = document.createElement('div');
    this.aimPanel.className = 'minigame-panel aim-panel hidden';
    this.aimPanel.innerHTML = `
      <div class="mg-header">
        <span class="mg-title">AIM BOOSTER</span>
        <span class="mg-timer" id="aim-timer">10.0s</span>
        <span class="mg-count" id="aim-count">0 / 15</span>
      </div>
      <div class="aim-area" id="aim-area"></div>
      <div class="mg-footer">Hit all 15 targets in 10s → +1 COMBO</div>
    `;
    container.appendChild(this.aimPanel);

    this.aimTimerEl = document.getElementById('aim-timer')!;
    this.aimCountEl = document.getElementById('aim-count')!;
    this.aimAreaEl = document.getElementById('aim-area')!;

    // Mental Math Panel
    this.mathPanel = document.createElement('div');
    this.mathPanel.className = 'minigame-panel math-panel hidden';
    this.mathPanel.innerHTML = `
      <div class="mg-header">
        <span class="mg-title">MENTAL MATH</span>
        <span class="mg-count" id="math-progress">0 / 10</span>
        <button class="btn mg-close interactive" id="math-close">X</button>
      </div>
      <div class="math-question" id="math-question"></div>
      <input type="text" class="math-input interactive" id="math-input" placeholder="?" autocomplete="off" />
      <div class="mg-footer">Solve all 10 → +1 LIFE (close & reopen anytime)</div>
    `;
    container.appendChild(this.mathPanel);

    this.mathQuestionEl = document.getElementById('math-question')!;
    this.mathInputEl = document.getElementById('math-input') as HTMLInputElement;
    this.mathProgressEl = document.getElementById('math-progress')!;

    // Wire math input
    this.mathInputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.checkMathAnswer();
    });

    document.getElementById('math-close')!.addEventListener('click', () => {
      this.closeMath();
    });

    // Keyboard shortcuts — ignore when typing in any input
    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement) return;
      if ((e.key === 'a' || e.key === 'A') && !this.aimActive && !this.mathOpen) {
        this.startAim();
      } else if ((e.key === 'm' || e.key === 'M') && !this.aimActive) {
        if (this.mathOpen) this.closeMath();
        else this.openMath();
      }
    });
  }

  // ── Aim Booster ──────────────────────────────────
  private startAim(): void {
    this.onOpened();
    this.aimActive = true;
    this.aimHit = 0;
    this.aimTimer = 10;
    this.aimPanel.classList.remove('hidden');
    this.aimAreaEl.innerHTML = '';
    this.aimTargets = [];
    this.updateAimUI();

    // Spawn all targets at once
    for (let i = 0; i < this.aimTotal; i++) {
      this.spawnAimTarget();
    }

    // Timer countdown
    this.aimTimerInterval = window.setInterval(() => {
      this.aimTimer -= 0.1;
      this.aimTimerEl.textContent = `${Math.max(0, this.aimTimer).toFixed(1)}s`;
      if (this.aimTimer <= 0) {
        this.endAim(false);
      }
    }, 100);
  }

  private spawnAimTarget(): void {
    const target = document.createElement('div');
    target.className = 'aim-target';
    const size = 20 + Math.random() * 20;
    target.style.width = `${size}px`;
    target.style.height = `${size}px`;
    target.style.left = `${10 + Math.random() * 80}%`;
    target.style.top = `${10 + Math.random() * 80}%`;

    target.addEventListener('click', () => {
      target.classList.add('aim-hit');
      setTimeout(() => target.remove(), 150);
      this.aimHit++;
      this.updateAimUI();
      if (this.aimHit >= this.aimTotal) {
        this.endAim(true);
      }
    });

    this.aimAreaEl.appendChild(target);
    this.aimTargets.push(target);
  }

  private endAim(success: boolean): void {
    clearInterval(this.aimTimerInterval);
    this.aimActive = false;
    this.aimPanel.classList.add('hidden');
    if (success) this.onComboEarned();
  }

  private updateAimUI(): void {
    this.aimCountEl.textContent = `${this.aimHit} / ${this.aimTotal}`;
  }

  // ── Mental Math ──────────────────────────────────
  private openMath(): void {
    this.onOpened();
    this.mathOpen = true;
    this.mathPanel.classList.remove('hidden');

    // Generate problems if not already generated
    if (this.mathProblems.length === 0) {
      this.generateMathProblems();
    }

    this.showCurrentMathProblem();
    setTimeout(() => this.mathInputEl.focus(), 50);
  }

  private closeMath(): void {
    this.mathOpen = false;
    this.mathPanel.classList.add('hidden');
  }

  private generateMathProblems(): void {
    this.mathProblems = [];
    this.mathSolved = 0;
    this.mathCurrentIdx = 0;

    for (let i = 0; i < this.mathTotal; i++) {
      const op = Math.random();
      let a: number, b: number, answer: number, question: string;

      if (op < 0.4) {
        // Addition
        a = 10 + Math.floor(Math.random() * 90);
        b = 10 + Math.floor(Math.random() * 90);
        answer = a + b;
        question = `${a} + ${b}`;
      } else if (op < 0.7) {
        // Subtraction
        a = 20 + Math.floor(Math.random() * 80);
        b = 5 + Math.floor(Math.random() * (a - 5));
        answer = a - b;
        question = `${a} - ${b}`;
      } else {
        // Multiplication
        a = 3 + Math.floor(Math.random() * 10);
        b = 3 + Math.floor(Math.random() * 10);
        answer = a * b;
        question = `${a} × ${b}`;
      }

      this.mathProblems.push({ question, answer });
    }
  }

  private showCurrentMathProblem(): void {
    if (this.mathCurrentIdx >= this.mathTotal) {
      this.mathQuestionEl.textContent = 'ALL DONE!';
      this.mathInputEl.style.display = 'none';
      return;
    }
    const p = this.mathProblems[this.mathCurrentIdx];
    this.mathQuestionEl.textContent = `${p.question} = ?`;
    this.mathInputEl.value = '';
    this.mathInputEl.focus();
    this.mathProgressEl.textContent = `${this.mathSolved} / ${this.mathTotal}`;
  }

  private checkMathAnswer(): void {
    if (this.mathCurrentIdx >= this.mathTotal) return;

    const userAnswer = parseInt(this.mathInputEl.value.trim(), 10);
    const correct = userAnswer === this.mathProblems[this.mathCurrentIdx].answer;

    if (correct) {
      this.mathSolved++;
      this.mathCurrentIdx++;
      this.mathProgressEl.textContent = `${this.mathSolved} / ${this.mathTotal}`;

      if (this.mathSolved >= this.mathTotal) {
        this.onLifeEarned();
        // Close immediately and reset for next round
        this.mathProblems = [];
        this.mathInputEl.style.display = '';
        this.closeMath();
      } else {
        this.showCurrentMathProblem();
      }
    } else {
      // Wrong — shake the input
      this.mathInputEl.classList.add('math-shake');
      setTimeout(() => this.mathInputEl.classList.remove('math-shake'), 400);
      this.mathInputEl.value = '';
    }
  }

  isActive(): boolean {
    return this.aimActive || this.mathOpen;
  }

  dispose(): void {
    clearInterval(this.aimTimerInterval);
    this.aimPanel.remove();
    this.mathPanel.remove();
  }
}
